
import React, { useMemo } from 'react';
import { useGetProcurements } from '@/hooks/use-procurement';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FileText, ReceiptText, Wallet } from 'lucide-react';
import { Procurement } from '@/types/database.types';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

const FinanceSpendAnalysis = () => {
  const { data: procurements, isLoading } = useGetProcurements();
  
  const approvedProcurements = useMemo(() => {
    if (!procurements) return [];
    
    // Type assertion to work with properly typed data
    return (procurements as Procurement[]).filter(p => 
      ['price_confirmed', 'quality_confirmed', 'agreement_signed', 
       'pickup_scheduled', 'harvested', 'in_transit', 'delivered', 'completed'].includes(p.status)
    );
  }, [procurements]);
  
  // Calculate total spend amount
  const totalSpend = useMemo(() => {
    return approvedProcurements.reduce((total, p) => total + (p.total_amount || 0), 0);
  }, [approvedProcurements]);

  // Generate spend by source type data
  const spendBySourceType = useMemo(() => {
    const sourceData: Record<string, number> = {};
    
    approvedProcurements.forEach(p => {
      if (!sourceData[p.source_type]) {
        sourceData[p.source_type] = 0;
      }
      sourceData[p.source_type] += (p.total_amount || 0);
    });
    
    return Object.entries(sourceData).map(([name, value]) => ({
      name,
      value
    }));
  }, [approvedProcurements]);

  // Generate spend by month data
  const spendByMonth = useMemo(() => {
    const monthData: Record<string, number> = {};
    
    approvedProcurements.forEach(p => {
      const month = format(new Date(p.created_at), 'MMM yyyy');
      if (!monthData[month]) {
        monthData[month] = 0;
      }
      monthData[month] += (p.total_amount || 0);
    });
    
    return Object.entries(monthData)
      .map(([month, amount]) => ({ month, amount }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA.getTime() - dateB.getTime();
      });
  }, [approvedProcurements]);

  // Generate spend by product data
  const spendByProduct = useMemo(() => {
    const productData: Record<string, number> = {};
    
    approvedProcurements.forEach(p => {
      if (!p.product_name) return;
      
      if (!productData[p.product_name]) {
        productData[p.product_name] = 0;
      }
      productData[p.product_name] += (p.total_amount || 0);
    });
    
    return Object.entries(productData)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value as number) - (a.value as number))
      .slice(0, 5); // Top 5 products
  }, [approvedProcurements]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <Wallet className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <h3 className="text-2xl font-bold">Br {totalSpend.toFixed(2)}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-green-100 rounded-full">
                <ReceiptText className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Procurements</p>
                <h3 className="text-2xl font-bold">{approvedProcurements.length}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <FileText className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Price Per Unit</p>
                <h3 className="text-2xl font-bold">
                  Br {approvedProcurements.length > 0 
                    ? (approvedProcurements.reduce((sum, p) => sum + (p.costs?.price_per_unit || 0), 0) / approvedProcurements.length).toFixed(2)
                    : "0.00"}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="monthly">
        <TabsList className="mb-4">
          <TabsTrigger value="monthly">Monthly Spend</TabsTrigger>
          <TabsTrigger value="source">By Source Type</TabsTrigger>
          <TabsTrigger value="product">By Product</TabsTrigger>
        </TabsList>
        
        <TabsContent value="monthly">
          <Card>
            <CardHeader>
              <CardTitle>Spend by Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {spendByMonth.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No spend data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={spendByMonth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `Br ${value}`} />
                      <Tooltip formatter={(value) => [`Br ${Number(value).toFixed(2)}`, 'Amount']} />
                      <Legend />
                      <Bar dataKey="amount" fill="#8884d8" name="Spend Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="source">
          <Card>
            <CardHeader>
              <CardTitle>Spend by Source Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {spendBySourceType.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No spend data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={spendBySourceType}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={150}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {spendBySourceType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`Br ${Number(value).toFixed(2)}`, 'Amount']} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="product">
          <Card>
            <CardHeader>
              <CardTitle>Top 5 Products by Spend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {spendByProduct.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    No spend data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={spendByProduct}
                      layout="vertical"
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => `Br ${value}`} />
                      <YAxis type="category" dataKey="name" width={100} />
                      <Tooltip formatter={(value) => [`Br ${Number(value).toFixed(2)}`, 'Amount']} />
                      <Legend />
                      <Bar dataKey="value" fill="#82ca9d" name="Spend Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinanceSpendAnalysis;
